import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from '../../contexts/NotificationsContextType';
import { isClientRole, isPrestataireRole, isAdminRole } from '../../utils/roleUtils';

// Icons as React components
import { ReactComponent as CommandesIcon } from "../../assets/icons/order-blanc.svg";
import { ReactComponent as AnnuaireIcon } from "../../assets/icons/client-list-blanc.svg";
import { ReactComponent as CatalogueIcon } from "../../assets/icons/catalogue-blanc.svg";
import { ReactComponent as ProfilIcon } from "../../assets/icons/profil-blanc.svg";
import { ReactComponent as DocumentsIcon } from "../../assets/icons/document-blanc.svg";
import { ReactComponent as HistoriqueIcon } from "../../assets/icons/historique.svg";
import { ReactComponent as ComptesIcon } from "../../assets/icons/gestion-compte-blanc.svg";
import { ReactComponent as SupportIcon } from "../../assets/icons/support-blanc.svg";
import { ReactComponent as LogoutIcon } from "../../assets/icons/logout-blanc.svg";
import { ReactComponent as LogoIcon } from "../../assets/logo/logo-centralis-blanc.svg";
import { ReactComponent as ClosingArrow } from "../../assets/closing-arrow.svg";
import { ReactComponent as LogoTypeIcon } from "../../assets/logo/logotype-centralis.svg";


import "./SideBar.css";

interface SidebarProps {
  unreadCommandes?: number; /* Notifications de commandes */
  onLogout: () => void;
  userName: string;
  organisationName: string;
  isCollapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  onLogout,
  userName,
  organisationName,
  isCollapsed,
  onCollapse
}) => {
  const navigate = useNavigate(); // Hook must be inside component
  const { user, logout } = useAuth();
  const location = useLocation();
  const { unreadCommandes = 0, unreadOperations = 0 } = useNotifications() || {};
  const [isUserAcheteur, setIsUserAcheteur] = useState(false);
  const [homeRoute, setHomeRoute] = useState('/');

  // Effet pour déterminer le type d'utilisateur à partir du localStorage
  useEffect(() => {
    const userDataString = localStorage.getItem('user');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        const userRole = userData.role;

        // Vérifier si c'est un acheteur selon le rôle frontend
        const isAcheteur = ["Acheteur", "Acheteur signataire", "Acheteur signataire admin"].includes(userRole);
        setIsUserAcheteur(isAcheteur);

        // Définir la route d'accueil
        setHomeRoute(isAcheteur ? '/operations' : '/commandes');
      } catch (error) {
        console.error("Erreur lors du parsing des données utilisateur:", error);
        setIsUserAcheteur(false);
        setHomeRoute('/');
      }
    }
  }, []);

  // Log pour debugging
  console.log("SideBar - Notifications:", { unreadCommandes, unreadOperations });
  console.log("SideBar - IsUserAcheteur:", isUserAcheteur);

  const toggleCollapse = () => {
    onCollapse(!isCollapsed);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getLogo = () => {
    if (isCollapsed) {
      return (
        <div className="logo-collapsed">
          <LogoTypeIcon />
        </div>
      );
    } else {
      return <LogoIcon />;
    }
  };

  // Construire les liens de la sidebar en fonction du type d'utilisateur
  const sidebarLinks = [
    {
      path: homeRoute, // Route dynamique basée sur le type d'utilisateur
      label: 'Accueil',
      icon: 'commandess-icon',
      show: true // Toujours affiché
    },
    {
      path: '/publications', // Route dynamique basée sur le type d'utilisateur
      label: 'Publications',
      icon: 'publications-icon',
      show: true // Toujours affiché
    },
    {
      path: '/commandes',
      label: 'Commandes',
      icon: 'commandes-icon',
      notifications: unreadCommandes,
      show: !isUserAcheteur // Uniquement pour les prestataires
    },
    // {
    //   path: '/publications',
    //   label: 'Publications',
    //   icon: 'publications-icon',
    //   show: true // Tous les utilisateurs
    // },
    {
      path: '/operations',
      label: 'Opérations',
      icon: 'operations-icon',
      notifications: unreadOperations,
      show: isUserAcheteur // Uniquement pour les acheteurs
    },
    {
      path: '/annuaire',
      label: 'Annuaire',
      icon: 'annuaire-icon',
      show: true // Tous les utilisateurs
    },
    {
      path: '/profil',
      label: 'Profil',
      icon: 'profil-icon',
      show: true // Tous les utilisateurs
    },
    {
      path: '/assistance',
      label: 'Assistance',
      icon: 'support-icon',
      show: true // Tous les utilisateurs
    },
    {
      path: '/aide',
      label: 'Aide',
      icon: 'catalogue-icon',
      show: true // Tous les utilisateurs
    }
  ];

  // Filtrer les liens en fonction de la propriété show
  const filteredLinks = sidebarLinks.filter(link => link.show);

  return (
    <aside className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <div className="first-section">
        <div className="sidebar-header">
          <div className="logo-section">
            {getLogo()}
          </div>
        </div>
        <ul className="sidebar-menu">
          {filteredLinks.map((link) => {
            // Determine which icon component to use based on the icon string
            let IconComponent;
            switch (link.icon) {
              case 'commandes-icon':
                IconComponent = CommandesIcon;
                break;
              case 'publications-icon':
                IconComponent = CommandesIcon;
                break;
              case 'operations-icon':
                IconComponent = CommandesIcon; // You may need to create or import an operations icon
                break;
              case 'annuaire-icon':
                IconComponent = AnnuaireIcon;
                break;
              case 'profil-icon':
                IconComponent = ProfilIcon;
                break;
              case 'support-icon':
                IconComponent = SupportIcon;
                break;
              case 'catalogue-icon':
                IconComponent = CatalogueIcon;
                break;
              default:
                IconComponent = DocumentsIcon; // Default icon
            }

            return (
              <li key={link.label}>
                <NavLink to={link.path} className="sidebar-link"
                >
                  <IconComponent className="icon" />
                  {!isCollapsed && <span>{link.label}</span>}
                  {typeof link.notifications === "number" && link.notifications > 0 && (
                    <span className="badge">{link.notifications}</span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
        <ul className="sidebar-footer">
          <li>
            <button onClick={handleLogout} className="sidebar-link logout-btn">
              <LogoutIcon className="icon" />
              {!isCollapsed && <span>Déconnexion</span>}
            </button>
          </li>
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;